// Volumetric single-layer cloud raymarch (rendered at reduced resolution).
//
// Per pixel: reconstruct the world-space view ray, intersect the horizontal
// cloud slab, march FBM density through it, light each sample with the
// scenario sun (cheap directional-derivative self-shadow) plus a hemispheric
// sky-tint ambient — the same HDRI approximation the particle/smoke shaders
// use — then apply the scene's linear fog. The march is clipped against scene
// depth (reconstructed from the logarithmic depth buffer) so terrain, the
// aircraft and other objects correctly occlude the deck.
//
// On top of the broad FBM mass sits an optional fine "wisp" field (uWisp*) that
// erodes the coverage feather at an absolute metre scale, independent of
// uNoiseScale — see applyWisps() for why it is nearly free.
//
// Output is premultiplied colour in rgb and coverage in alpha, composited over
// the beauty pass by cloud_composite_frag.glsl.

varying vec2 vUv;

uniform sampler2D uSceneDepth;   // scene depth (logarithmic buffer)
uniform sampler2D uNoise;        // packed 2D value-noise (IQ 3D-from-2D trick)
uniform sampler2D uBlueNoise;    // 64×64 blue-noise dither for the ray-start offset

// Temporal reprojection accumulation. Each frame samples the slab with a
// different ray-start offset (uJitter advances the blue-noise value along a
// golden-ratio sequence) and blends the result into the previous frame's
// low-res buffer, reprojected by the previous camera's world→clip matrix. Over
// a handful of frames this converges to many times the per-frame sample count,
// dissolving the raymarch grain without raising per-frame cost.
uniform sampler2D uPrevHistory;  // previous frame's accumulated low-res buffer
uniform mat4  uPrevViewProj;     // previous frame's world -> clip
uniform float uHistoryValid;     // 0 = ignore history (first frame / after a cut)
uniform float uBlend;            // EMA weight of the current sample (0..1)
uniform float uJitter;           // per-frame ray-start offset in [0,1) (golden ratio)

// Camera / ray reconstruction
uniform vec3  uCameraPos;
uniform mat4  uInvProjection;    // clip  -> view
uniform mat4  uInvView;          // view  -> world  (camera.matrixWorld)
uniform mat4  uViewMatrix;       // world -> view   (camera.matrixWorldInverse)
uniform float uCameraFar;

uniform float uTime;

// Layer geometry
uniform float uCloudBase;        // metres ASL
uniform float uCloudTop;         // metres ASL
uniform float uDrawDist;         // metres — horizontal reach of the deck
uniform float uNoiseScale;       // metres per base FBM octave wrap
uniform vec3  uWindDir;          // normalized drift direction
uniform float uWindSpeed;        // m/s

// Shape
uniform float uCoverage;         // 0..1 fraction of sky filled
uniform float uDensity;          // extinction (opacity) multiplier
uniform float uEdgeSoftness;     // coverage-threshold feather width
uniform float uNearFade;         // metres — fade the deck in near the camera

// Fine wisp detail — a second, much finer field that ERODES the base mass rather
// than adding to it, fraying edges into filaments and punching pinholes. Its
// scale is in absolute metres, so it is completely decoupled from uNoiseScale:
// resizing the broad systems never resizes the wisps.
uniform float uWispStrength;     // erosion depth, 0..1 (0 = off)
uniform float uWispScale;        // metres per wrap of the first wisp octave
uniform float uWispDrift;        // wisp drift as a multiple of uWindSpeed
uniform vec2  uWispFade;         // metres — (full detail by x, gone by y)

// Lighting
uniform vec3  uAlbedo;           // cloud albedo (linear)
uniform vec3  uSunDir;           // toward the sun (world, normalized)
uniform vec3  uSunColor;         // linear, already scaled by intensity
uniform float uSunScatter;       // sun diffuse strength (0 disables the sun sample)
uniform vec3  uSkyTop;           // hemispheric sky irradiance, upper (linear)
uniform vec3  uSkyBottom;        // hemispheric sky irradiance, lower (linear)
uniform float uAmbient;          // ambient strength multiplier
uniform float uEffectBrightness; // per-scenario env-lit dim (matches particles)

// Fog (matches THREE.Fog)
uniform vec3  uFogColor;
uniform float uFogNear;
uniform float uFogFar;

#ifndef CLOUD_STEPS
#define CLOUD_STEPS 32
#endif
#ifndef CLOUD_OCTAVES
#define CLOUD_OCTAVES 5
#endif
// CLOUD_WISPS is defined by CloudLayer only when CLOUDS.wisp.enabled, so the
// whole detail path compiles out when it's off.
#ifndef CLOUD_WISP_OCTAVES
#define CLOUD_WISP_OCTAVES 2
#endif

// 3D value noise from a packed 2D texture (Íñigo Quílez's trick): the .yx read
// gives two z-slices that mix by the fractional z. The texture is baked so the
// slices stay continuous across integer z (see CloudLayer.buildNoiseTexture).
float noise(vec3 x) {
  vec3 p = floor(x);
  vec3 f = fract(x);
  f = f * f * (3.0 - 2.0 * f);
  vec2 uv = (p.xy + vec2(37.0, 239.0) * p.z) + f.xy;
  vec2 tex = texture2D(uNoise, (uv + 0.5) / 256.0).yx;
  return mix(tex.x, tex.y, f.z) * 2.0 - 1.0;
}

float fbm(vec3 p) {
  float f = 0.0;
  float scale = 0.5;
  float factor = 2.02;
  for (int i = 0; i < CLOUD_OCTAVES; i++) {
    f += scale * noise(p);
    p *= factor;
    factor += 0.21;
    scale *= 0.5;
  }
  return f;
}

#ifdef CLOUD_WISPS
// Billow FBM in [0,1] used as an EROSION amount. |noise| folds the field about
// its zero crossings, so it dips to 0 along thin surfaces and rises between
// them; subtracting it eats everything except those surfaces, leaving fine
// filaments rather than the blobby holes a plain FBM would carve.
//
// noise() is exactly 256-periodic on every axis (the packed z offsets 37/239 are
// both multiples of 256 after a full wrap), so folding the coordinate back into
// [0,256) before each tap is visually free — and necessary here, because at
// these frequencies world coordinates would otherwise reach magnitudes where
// noise()'s internal `p.z * 239` quantises the fract() into stair-steps.
float wispFbm(vec3 p) {
  float f = 0.0;
  float amp = 0.5;
  float norm = 0.0;
  for (int i = 0; i < CLOUD_WISP_OCTAVES; i++) {
    f += amp * abs(noise(mod(p, 256.0)));
    norm += amp;
    p *= 2.63;
    amp *= 0.5;
  }
  return f / norm;
}

// Erode a base density with the wisp field. `w` is the already-faded erosion
// depth (master strength × distance fade); the remap keeps 0 at 0 and 1 at 1, so
// only the coverage feather in between is reshaped.
//
// The two guards are the whole performance story: d is exactly 1.0 inside a
// cloud core and exactly 0.0 in open sky (smoothstep saturates), so only the
// feathered band pays the extra texture fetches — and w is 0 past uWispFade.y,
// where the wisps would be sub-pixel anyway.
float applyWisps(float d, vec3 p, float w) {
  if (w <= 0.0 || d <= 0.0 || d >= 1.0) return d;
  vec3 wp = p / uWispScale + uWindDir * (uTime * uWindSpeed * uWispDrift / uWispScale);
  float e = wispFbm(wp) * w;
  return clamp((d - e) / max(1.0 - e, 0.001), 0.0, 1.0);
}
#endif

// Cloud density [0,1] at a world point. FBM shape thresholded by coverage, with
// an adjustable edge feather. The vertical shaping is deliberately asymmetric:
// a soft feather across the base face only (so cloud bottoms can stay flat),
// while the top is shaped by eroding the coverage threshold with height rather
// than by a symmetric fade — that keeps tops following the noise into billows
// so they can never clip flat against the top face.
//
// This is the low-frequency mass only — fine wisps are layered on top by
// applyWisps() at the march site, so the sun's self-shadow tap can skip them.
float sampleDensity(vec3 p) {
  float t = (p.y - uCloudBase) / max(uCloudTop - uCloudBase, 1.0);
  // Soft feather across the base face only — 0 below the slab, 1 by ~12% up.
  float baseFade = smoothstep(0.0, 0.12, t);
  if (baseFade <= 0.0) return 0.0;

  vec3 drift = uWindDir * (uTime * uWindSpeed / uNoiseScale);
  float shape = fbm(p / uNoiseScale + drift) * 0.5 + 0.5;

  // Raise the coverage threshold toward the top of the slab: the lower body
  // keeps the full coverage (flat bases survive), while the upper part demands
  // progressively denser noise to pass — rounding the tops into billows. At the
  // very top the threshold reaches 1.0, so nothing clips flat against the face.
  float lo = 1.0 - uCoverage;
  lo = mix(lo, 1.0, smoothstep(0.25, 1.0, clamp(t, 0.0, 1.0)));
  float d = smoothstep(lo, min(lo + uEdgeSoftness, 1.0), shape);

  return d * baseFade;
}

void main() {
  // Reconstruct the world-space ray for this pixel from the inverse matrices.
  vec4 clip = vec4(vUv * 2.0 - 1.0, 1.0, 1.0);
  vec4 vpos = uInvProjection * clip;
  vpos /= vpos.w;
  vec3 rd = normalize(mat3(uInvView) * normalize(vpos.xyz));
  vec3 ro = uCameraPos;

  // Intersect the horizontal slab [uCloudBase, uCloudTop].
  float t0, t1;
  if (abs(rd.y) < 1e-4) {
    // Ray parallel to the deck: only inside the band do we march.
    if (ro.y < uCloudBase || ro.y > uCloudTop) { gl_FragColor = vec4(0.0); return; }
    t0 = 0.0; t1 = uDrawDist;
  } else {
    float ta = (uCloudBase - ro.y) / rd.y;
    float tb = (uCloudTop  - ro.y) / rd.y;
    t0 = min(ta, tb);
    t1 = max(ta, tb);
  }

  // Nearest occluder distance from the scene depth buffer. Cleared/far pixels
  // (sky) read ~1.0 and leave the march running to the full draw distance.
  float sceneD = texture2D(uSceneDepth, vUv).x;
  float tScene = uDrawDist;
  if (sceneD < 0.9999) {
    // Invert the logarithmic depth: window depth d -> perpendicular eye distance.
    float w = exp2(sceneD * log2(uCameraFar + 1.0)) - 1.0;
    // Convert that perpendicular distance to a distance along this ray.
    float wSlope = -dot(vec3(uViewMatrix[0][2], uViewMatrix[1][2], uViewMatrix[2][2]), rd);
    tScene = (wSlope > 1e-4) ? (w / wSlope) : uDrawDist;
  }

  float tEnter = max(t0, 0.0);
  float tExit  = min(t1, min(tScene, uDrawDist));
  if (tExit <= tEnter) { gl_FragColor = vec4(0.0); return; }

  float stepSize = (tExit - tEnter) / float(CLOUD_STEPS);

  // Hemispheric sky-tint ambient — identical form to particle_frag.glsl.
  vec3 ambient = mix(uSkyBottom, uSkyTop, 0.7);
  ambient = ambient / (vec3(1.0) + ambient);
  ambient *= 2.0 * uEffectBrightness * uAmbient;

  float lightStep = max(uNoiseScale * 0.12, 20.0);
  // Blue-noise ray-start jitter (0..1 step) — its structureless high-frequency
  // spectrum dithers the fixed step count into fine grain instead of the shells /
  // stripes a structured (e.g. gradient-noise) dither leaves behind once upscaled.
  // 64.0 must match the tile size baked in CloudLayer.buildBlueNoiseTexture().
  float dither = texture2D(uBlueNoise, gl_FragCoord.xy / 64.0).x;
  // Advance the spatial blue-noise value by this frame's golden-ratio offset:
  // the offsets stay well-distributed in time (low discrepancy) while keeping
  // their blue spatial spectrum, so temporal accumulation converges fast.
  dither = fract(dither + uJitter);
  float t = tEnter + stepSize * dither;

  float T = 1.0;          // transmittance
  vec3  C = vec3(0.0);    // accumulated premultiplied colour
  float wSum = 0.0;       // coverage-weighted distance (fog)
  float aSum = 0.0;

  for (int i = 0; i < CLOUD_STEPS; i++) {
    if (t > tExit || T < 0.02) break;
    vec3 p = ro + rd * t;
    // `mass` is the broad low-frequency density; `dens` is that same sample with
    // the fine wisps eroded out of it. Extinction uses the eroded value (so wisps
    // read thin and translucent) while the lighting below stays keyed to the mass
    // — otherwise a thinning filament would darken as it frayed, instead of being
    // lit like the cloud it hangs off.
    float mass = sampleDensity(p);
    float dens = mass;
    #ifdef CLOUD_WISPS
      dens = applyWisps(mass, p, uWispStrength * (1.0 - smoothstep(uWispFade.x, uWispFade.y, t)));
    #endif
    if (dens > 0.001) {
      vec3 lit = uAlbedo * ambient;
      if (uSunScatter > 0.0) {
        // Directional derivative toward the sun ≈ how sunlit this facet is.
        float densSun = sampleDensity(p + uSunDir * lightStep);
        float diff = clamp((mass - densSun) / mass, 0.0, 1.0);
        lit += uAlbedo * uSunColor * (uSunScatter * diff);
      }
      // Fog each sample by its OWN distance before compositing. This is what stops
      // overlapping near/far cloud edges from fighting: applied once at the end (as
      // it used to be), the fog keyed off the transmittance-weighted mean distance,
      // which is bimodal at such pixels — tiny when the jitter catches the near
      // wisp, huge when it only catches the far mass. The fog amount, and thus the
      // colour, then flickered between "near and bright" and "far and hazy". Fogging
      // per sample blends the two stably regardless of which the jitter emphasises.
      float fogS = clamp((uFogFar - t) / max(uFogFar - uFogNear, 1.0), 0.0, 1.0);
      lit = mix(uFogColor, lit, fogS);
      float a = 1.0 - exp(-dens * uDensity * stepSize);
      C    += T * a * lit;
      wSum += T * a * t;
      aSum += T * a;
      T    *= (1.0 - a);
    }
    t += stepSize;
  }

  float alpha = 1.0 - T;
  if (alpha < 0.003) { gl_FragColor = vec4(0.0); return; }

  // Mean cloud distance (transmittance-weighted) — used only for the near-fade
  // now that fog is applied per sample inside the march above.
  float meanDist = aSum > 0.0 ? wSum / aSum : tEnter;

  // Fade the deck in near the camera so passing through a face doesn't pop.
  float nearFade = smoothstep(0.0, uNearFade, meanDist);
  alpha *= nearFade;
  C     *= nearFade;

  vec4 current = vec4(C, alpha);

  // ── Temporal reprojection accumulation ──────────────────────────────────────
  // Reproject this pixel's cloud point (world position at the mean cloud depth)
  // through the previous camera to find where it sat last frame, then blend the
  // fresh sample into that history. Both buffers hold premultiplied colour, so a
  // straight lerp is valid. Sky/empty pixels take the early-outs above and write
  // 0, which keeps history from trailing behind receding cloud edges.
  vec4 outColor = current;
  if (uHistoryValid > 0.5) {
    vec3 worldPos = ro + rd * meanDist;
    vec4 pc = uPrevViewProj * vec4(worldPos, 1.0);
    if (pc.w > 0.0) {
      vec2 pUV = pc.xy / pc.w * 0.5 + 0.5;
      if (all(greaterThanEqual(pUV, vec2(0.0))) && all(lessThanEqual(pUV, vec2(1.0)))) {
        vec4 hist = texture2D(uPrevHistory, pUV);
        // Snap toward the current sample where coverage changed sharply — a cloud
        // edge sweeping past or a disocclusion — so stale history can't smear.
        float w = mix(uBlend, 1.0, smoothstep(0.1, 0.5, abs(hist.a - current.a)));
        outColor = mix(hist, current, w);
      }
    }
  }

  gl_FragColor = outColor;
}
